import { c as _c } from "react/compiler-runtime";
import { CreditCardCheckIcon, CreditCardRecurringPaymentIcon, CreditCardUpIcon, CreditCardXIcon, ShoppingBag02Icon, UserPlus01Icon } from '@systemeio/icons';
import { centsToUnits } from '@systemeio/util';
import LazyContactInfoTooltip from 'modules/contacts/components/lazy-contact-info-tooltip';
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url';
import { MainEventsEnum, mainEventsType } from 'modules/main/enums/main-events-enum';
import { EventChargeInterface, EventContactCreatedInterface, EventPaymentInterface, MainEventType } from 'modules/main/types/main-interface';
import React, { type JSX, type ReactNode } from 'react';
import { IconTile } from 'shared/components/icon-tile';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { CompactTime, RelativeTime } from 'shared/components/relative-time';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url';
interface RenderEventProps {
  event: MainEventType;
}
const eventIcon: Record<MainEventsEnum, JSX.Element> = {
  [MainEventsEnum.contact_created]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <UserPlus01Icon />
    </IconTile>,
  [MainEventsEnum.new_signup]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <UserPlus01Icon />
    </IconTile>,
  [MainEventsEnum.recurring_charge_succeed]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardRecurringPaymentIcon />
    </IconTile>,
  [MainEventsEnum.one_shot_charge_succeed]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <ShoppingBag02Icon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_stopped]: <IconTile size={'s'} aria-hidden="true" tone="gray">
      <CreditCardXIcon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_completed]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardCheckIcon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_created]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardUpIcon />
    </IconTile>,
  [MainEventsEnum.recurring_charge_failed]: <IconTile size={'s'} aria-hidden="true" tone="danger">
      <CreditCardXIcon />
    </IconTile>
};
type OrderItem = EventChargeInterface['relatedObject']['orderItem'] | EventPaymentInterface['relatedObject']['orderItem'];
type Customer = OrderItem['order']['customer'];
interface EventLines {
  who: ReactNode;
  what?: ReactNode;
}
const getOfferName = (orderItem: OrderItem) => {
  if (orderItem.pricePlan) {
    return orderItem.pricePlan.innerName;
  }
  if (orderItem.productOrderItem) {
    return orderItem.productOrderItem.product.name;
  }
  return '';
};
const getCustomerName = (customer: Customer) => customer.fullName || customer.contact.connectedUser?.fullName || customer.contact.email;
const getContactLink = (contact: {
  id: number;
  removedAt: string | null;
}, name: string): JSX.Element => <LazyContactInfoTooltip contactId={contact.id} removedAt={contact.removedAt}>
    {name}
  </LazyContactInfoTooltip>;
const getOfferLink = (orderItem: OrderItem): ReactNode => {
  const offerName = getOfferName(orderItem);
  const funnelStep = orderItem.offer?.funnelStep;
  if (!offerName || !funnelStep) return offerName;
  return <LinkWithoutPrefetch href={getFunnelStepUrl(funnelStep.funnel.id, funnelStep.id)} className={'hover:underline'}>
      {offerName}
    </LinkWithoutPrefetch>;
};
const getSourcePageHref = ({
  funnelStep,
  blogPage
}: NonNullable<EventContactCreatedInterface['relatedObject']['sourcePage']>) => {
  if (funnelStep) return getFunnelStepUrl(funnelStep.funnel.id, funnelStep.id);
  if (blogPage) return getBlogPostNextjsUrl(blogPage.blog.id);
  return '';
};
function RenderEvent(t0) {
  const $ = _c(29);
  const {
    event
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== event || $[1] !== user?.dashboardLocale) {
    const getEventLines = () => {
      switch (event.type) {
        case MainEventsEnum.new_signup:
          {
            const {
              relatedObject: relatedObject_0
            } = event;
            return {
              who: getContactLink(relatedObject_0, relatedObject_0.email)
            };
          }
        case MainEventsEnum.contact_created:
          {
            const {
              relatedObject
            } = event;
            const {
              sourcePage,
              pageTitle
            } = relatedObject;
            const href = sourcePage && getSourcePageHref(sourcePage);
            return {
              who: getContactLink(relatedObject, relatedObject.email),
              what: sourcePage && (href ? <LinkWithoutPrefetch href={href} className="hover:underline">{pageTitle}</LinkWithoutPrefetch> : pageTitle)
            };
          }
        case MainEventsEnum.recurring_charge_succeed:
        case MainEventsEnum.one_shot_charge_succeed:
          {
            const {
              orderItem: orderItem_0,
              payment,
              amount
            } = event.relatedObject;
            const {
              customer: customer_0
            } = orderItem_0.order;
            const offerLink = getOfferLink(orderItem_0);
            return {
              who: getContactLink(customer_0.contact, getCustomerName(customer_0)),
              what: <>{currencyFormat(centsToUnits(amount), payment.currency, user?.dashboardLocale)}{offerLink && <> • {offerLink}</>}</>
            };
          }
        case MainEventsEnum.payment_subscription_created:
        case MainEventsEnum.recurring_charge_failed:
        case MainEventsEnum.payment_subscription_stopped:
        case MainEventsEnum.payment_subscription_completed:
          {
            const {
              orderItem
            } = event.relatedObject;
            const {
              customer
            } = orderItem.order;
            return {
              who: getContactLink(customer.contact, getCustomerName(customer)),
              what: getOfferLink(orderItem)
            };
          }
      }
    };
    t1 = getEventLines();
    $[0] = event;
    $[1] = user?.dashboardLocale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    who,
    what
  } = t1;
  const t2 = eventIcon[event.type];
  const t3 = mainEventsType[event.type];
  let t4;
  if ($[3] !== t || $[4] !== t3) {
    t4 = t(t3);
    $[3] = t;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span className="text-text-primary truncate font-medium">{t4}</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== event.createdAt) {
    t6 = <span className="desktop:hidden"><CompactTime time={event.createdAt} /></span>;
    $[8] = event.createdAt;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== event.createdAt) {
    t7 = <span className="max-desktop:hidden"><RelativeTime time={event.createdAt} /></span>;
    $[10] = event.createdAt;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <span className="text-text-tertiary desktop:min-w-[4em] desktop:flex-1 desktop:truncate desktop:text-right text-sm">{t6}{t7}</span>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t8) {
    t9 = <div className="tablet:flex-row tablet:flex-wrap tablet:items-baseline tablet:justify-between desktop:flex-nowrap flex flex-col gap-x-2">{t5}{t8}</div>;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== who) {
    t10 = <span className="text-text-secondary truncate">{who}</span>;
    $[18] = who;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== what) {
    t11 = what && <span className="text-text-secondary truncate">{what}</span>;
    $[20] = what;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t2) {
    t13 = <div className="flex gap-2">{t2}{t12}</div>;
    $[26] = t12;
    $[27] = t2;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
export default RenderEvent;